/**
 * Workspace milestones tab (FRIDAY-65).
 *
 * Milestones are board-owned, so this screen is an AGGREGATION: it lists the
 * milestones of every board the caller may read, each labelled with its
 * owning board, and every mutation targets the milestone's own board.
 */

import { useEffect, useState } from 'react'
import { ApiError } from '@/lib/api'
import { toast } from '@/features/feedback/toast'
import { formatDueDate } from '@/features/milestones/milestones'
import {
  useArchiveMilestone,
  useCreateMilestone,
  useUpdateMilestone,
  useWorkspaceMilestones,
  type WorkspaceMilestone,
  type WorkspaceMilestoneBoard,
} from '@/features/milestones/milestones'
import { NavigationGuardProvider, useUnsavedChangesGuard } from '@/features/settings/useUnsavedChangesGuard'
import { ConfirmDialog } from '@/components/feedback'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 25

export interface WorkspaceMilestonesPanelProps {
  wsId: string
  /**
   * Locks the panel to one board. The board settings screen uses it to
   * show the milestones of that board; the workspace settings leave it
   * open.
   */
  projectId?: string
}

export function WorkspaceMilestonesPanel({ wsId, projectId }: WorkspaceMilestonesPanelProps) {
  return (
    <NavigationGuardProvider>
      <WorkspaceMilestonesPanelBody wsId={wsId} projectId={projectId} />
    </NavigationGuardProvider>
  )
}

function WorkspaceMilestonesPanelBody({ wsId, projectId: fixedProjectId }: WorkspaceMilestonesPanelProps) {
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  const [projectId, setProjectId] = useState(fixedProjectId ?? '')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const milestonesQ = useWorkspaceMilestones(wsId, {
    q: term,
    projectId: projectId || undefined,
    includeArchived,
    limit: PAGE_SIZE,
    offset,
  })

  const boards = milestonesQ.data?.boards ?? []
  const milestones = milestonesQ.data?.milestones ?? []
  const total = milestonesQ.data?.total ?? 0

  // A filter pointing at a board the caller can no longer see is cleared
  // instead of leaving an empty screen behind.
  useEffect(() => {
    if (!projectId || fixedProjectId || !milestonesQ.data) return
    if (!boards.some((b) => b.id === projectId)) setProjectId('')
  }, [projectId, boards, milestonesQ.data])

  useEffect(() => {
    setOffset(0)
  }, [term, projectId, includeArchived])

  if (milestonesQ.isLoading) {
    return (
      <p className="text-surface-500 p-4 text-sm" role="status">
        Loading milestones…
      </p>
    )
  }

  if (milestonesQ.isError) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-red-600 dark:text-red-300">
          Could not load the milestones of this workspace.
        </p>
        <button type="button" className="btn-ghost mt-2" onClick={() => void milestonesQ.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  const boardById = new Map(boards.map((b) => [b.id, b] as const))

  return (
    <div className="space-y-4">
      <p className="text-surface-500 text-xs">
        Milestones belong to a board — they are shown here from every board in this workspace. Editing a
        milestone changes it on its own board.
      </p>

      <section className="card p-4" aria-label="Filter milestones">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[12rem]">
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Search</span>
            <input
              type="search"
              className="input mt-1"
              placeholder="Milestone name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label>
            <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Board</span>
            <select
              className="input mt-1"
              value={projectId}
              disabled={!!fixedProjectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">All boards</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.key})
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pb-2 text-xs text-surface-600 dark:text-surface-300">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
      </section>

      <CreateMilestoneSection wsId={wsId} boards={boards} defaultProjectId={projectId} />

      {milestones.length === 0 ? (
        <div className="rounded-lg border border-dashed border-surface-200 p-12 text-center text-sm text-surface-500 dark:border-surface-700">
          No milestones yet. Group tasks into milestones to track what ships when.
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Milestones">
          {milestones.map((m) => (
            <MilestoneRow key={m.id} wsId={wsId} milestone={m} board={boardById.get(m.projectId)} />
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-surface-500">
          <span>
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={offset === 0 || milestonesQ.isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={offset + PAGE_SIZE >= total || milestonesQ.isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </button>
          </span>
        </div>
      )}
    </div>
  )
}

/** Create a milestone on one of the boards the caller may write to. */
function CreateMilestoneSection({
  wsId,
  boards,
  defaultProjectId,
}: {
  wsId: string
  boards: WorkspaceMilestoneBoard[]
  defaultProjectId: string
}) {
  const writable = boards.filter((b) => b.canWrite)
  const [projectId, setProjectId] = useState(defaultProjectId)
  const [picked, setPicked] = useState(false)
  const [name, setName] = useState('')
  const [due, setDue] = useState('')
  const [color, setColor] = useState('#5B5BD6')
  const [error, setError] = useState<string | null>(null)
  const create = useCreateMilestone(wsId, projectId)

  useEffect(() => {
    if (picked) return
    setProjectId(defaultProjectId)
  }, [defaultProjectId, picked])

  const dirty = name.trim().length > 0
  useUnsavedChangesGuard(dirty && !create.isPending)

  if (writable.length === 0) {
    return (
      <p className="text-surface-500 text-xs">
        You cannot create milestones here: none of the boards in this workspace accept milestone changes for
        your role.
      </p>
    )
  }

  return (
    <section className="card p-4" aria-label="Create milestone">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-surface-500">New milestone</h2>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!name.trim() || !projectId) return
          setError(null)
          try {
            await create.mutateAsync({
              name: name.trim(),
              dueDate: due ? new Date(due).toISOString() : null,
              color,
            })
            setName('')
            setDue('')
            toast('Milestone created', { kind: 'success' })
          } catch (err) {
            const msg = err instanceof ApiError ? err.message : (err as Error).message
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      >
        <label>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Board</span>
          <select
            className="input mt-1"
            value={projectId}
            onChange={(e) => {
              setPicked(true)
              setProjectId(e.target.value)
            }}
          >
            <option value="">Pick a board…</option>
            {writable.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.key})
              </option>
            ))}
          </select>
        </label>
        <label className="flex-1 min-w-[12rem]">
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Name</span>
          <input
            className="input mt-1"
            placeholder="e.g. Q1 launch"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Due date</span>
          <input
            type="date"
            className="input mt-1"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </label>
        <label>
          <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">Colour</span>
          <input
            type="color"
            aria-label="Colour"
            className="mt-1 h-9 w-12 cursor-pointer rounded border border-surface-300 dark:border-surface-700"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </label>
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending || !name.trim() || !projectId}
        >
          {create.isPending ? 'Creating…' : 'Add milestone'}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}
    </section>
  )
}

/** One milestone: name, status, due date, progress and the actions. */
function MilestoneRow({
  wsId,
  milestone,
  board,
}: {
  wsId: string
  milestone: WorkspaceMilestone
  board?: WorkspaceMilestoneBoard
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(milestone.name)
  const [error, setError] = useState<string | null>(null)
  const [pendingArchive, setPendingArchive] = useState(false)
  const update = useUpdateMilestone(wsId, milestone.projectId)
  const archive = useArchiveMilestone(wsId, milestone.projectId)

  const canWrite = board?.canWrite ?? false
  const dirty = editing && name.trim() !== milestone.name
  useUnsavedChangesGuard(dirty && !update.isPending)

  const total = milestone.taskCount
  const done = milestone.doneCount
  const ratio = total > 0 ? done / total : 0

  function cancel() {
    setName(milestone.name)
    setEditing(false)
    setError(null)
  }

  async function save() {
    if (!dirty) return
    setError(null)
    try {
      await update.mutateAsync({ id: milestone.id, patch: { name: name.trim() } })
      setEditing(false)
      toast('Milestone updated', { kind: 'success' })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not update the milestone.'
      setError(msg)
      toast(msg, { kind: 'error' })
    }
  }

  function patch(fields: { status?: WorkspaceMilestone['status']; dueDate?: string | null }) {
    update.mutate(
      { id: milestone.id, patch: fields },
      {
        onSuccess: () => toast('Milestone updated', { kind: 'success' }),
        onError: (e) => {
          const msg = e instanceof ApiError ? e.message : 'Could not update the milestone.'
          setError(msg)
          toast(msg, { kind: 'error' })
        },
      },
    )
  }

  return (
    <li className="card p-3" data-testid="workspace-milestone">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: milestone.color }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              className="input h-7 py-0 text-sm"
              aria-label={`Name for ${milestone.name}`}
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          ) : (
            <div className="truncate text-sm font-semibold">
              {milestone.name}
              {milestone.archivedAt && (
                <span className="ml-2 rounded bg-surface-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-surface-600 dark:bg-surface-700 dark:text-surface-300">
                  Archived
                </span>
              )}
            </div>
          )}
          <div className="text-xs text-surface-500">
            {milestone.projectName} ({milestone.projectKey}) · {formatDueDate(milestone.dueDate)}
          </div>
          {total > 0 ? (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-surface-200 dark:bg-surface-700">
                <div
                  className="h-1.5 rounded-full bg-brand-500 transition-all"
                  style={{ width: `${Math.round(ratio * 100)}%` }}
                />
              </div>
              <span className="font-mono text-[10px] text-surface-500">
                {done}/{total}
              </span>
            </div>
          ) : (
            <div className="mt-1 text-[10px] text-surface-500">No tasks attached</div>
          )}
        </div>

        {canWrite && (
          <div className="flex items-center gap-1">
            <select
              aria-label={`Status for ${milestone.name}`}
              value={milestone.status}
              onChange={(e) => patch({ status: e.target.value as WorkspaceMilestone['status'] })}
              className={cn(
                'rounded-full border-0 px-1.5 py-0.5 text-[10px] font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-500',
                milestone.status === 'completed' && 'bg-green-500/10 text-green-700 dark:text-green-300',
                milestone.status === 'in_progress' && 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
                milestone.status === 'missed' && 'bg-red-500/10 text-red-700 dark:text-red-300',
                milestone.status === 'planned' && 'bg-surface-200 text-surface-600',
              )}
            >
              <option value="planned">planned</option>
              <option value="in_progress">in_progress</option>
              <option value="completed">completed</option>
              <option value="missed">missed</option>
            </select>
            <button type="button" className="btn-ghost min-h-11 text-xs" onClick={() => setEditing(true)}>
              Rename
            </button>
            <button
              type="button"
              className="btn-ghost min-h-11 text-xs text-red-600 dark:text-red-300"
              onClick={() => setPendingArchive(true)}
            >
              Archive
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary min-h-11 text-xs"
            disabled={update.isPending || !dirty}
            onClick={() => void save()}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-ghost min-h-11 text-xs" disabled={update.isPending} onClick={cancel}>
            Cancel
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={pendingArchive}
        title={`Archive "${milestone.name}"?`}
        body={
          <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
            <p>
              The milestone will be hidden from {milestone.projectName} ({milestone.projectKey}) and any task
              assigned to it will be detached — the task stays, but it no longer belongs to a milestone.
            </p>
            <p className="text-surface-500 text-xs">
              {total === 0
                ? 'No task is attached to this milestone.'
                : `${total} ${total === 1 ? 'task is' : 'tasks are'} attached to it right now.`}
            </p>
          </div>
        }
        confirmLabel="Archive milestone"
        destructive
        busy={archive.isPending}
        onClose={() => !archive.isPending && setPendingArchive(false)}
        onConfirm={async () => {
          try {
            await archive.mutateAsync(milestone.id)
            setPendingArchive(false)
            toast(`Milestone "${milestone.name}" archived`, { kind: 'success' })
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Could not archive the milestone.'
            setError(msg)
            toast(msg, { kind: 'error' })
          }
        }}
      />
    </li>
  )
}
